import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// Eager, unlike the library itself: MapLibre's controls, marker and popup are
// unstyled without it, and a stylesheet cannot be pulled in from inside the async
// import without a flash of broken chrome. ~40KB, and it is the only eager part.
import 'maplibre-gl/dist/maplibre-gl.css';
import { api } from '../lib/api';
import { Button } from '../atoms';
import { useToast } from './Toast';
import { fallbackViewFor, STATE_ZOOM } from '../lib/stateCentroids';

/**
 * PropertyMapCard — where one property physically is.
 *
 * Asked for at the Pepco Jul-15 demo (Eric Nelson). Coordinates live on
 * `properties` (migration 134) — the property is the physical site, and several
 * parcels routinely share it.
 *
 * IT NEVER DEFAULTS TO NOTHING. The basemap is OpenFreeMap, which is open and
 * keyless, so any address that can be geocoded gets a map — on every environment,
 * with no configuration. The previous version was gated on a Mapbox token that no
 * environment had, so it rendered a "map unavailable" panel everywhere while
 * production held 55 addressed properties and zero coordinates.
 *
 * ⚠️ STREET, NOT AERIAL — SAID OUT LOUD RATHER THAN SUBSTITUTED SILENTLY.
 * Eric asked for an aerial view. OpenFreeMap is a STREET basemap; there is no free
 * aerial layer we can point at without a licence review, so shipping streets and
 * calling it "Aerial view" would be a quiet downgrade someone discovers in front of
 * a customer. The panel is titled "Location" and aerial remains an upgrade: set a
 * Mapbox token and `satellite-streets` can be added as a raster layer over this
 * same map without touching anything else here.
 *
 * WHY MAPLIBRE NOW, WHEN THE RASTER GRID NEEDED NO DEPENDENCY
 * ----------------------------------------------------------
 * Parcel BOUNDARIES are polygons served as PMTiles, and a polygon cannot be drawn
 * on a grid of <img> tiles. The moment the parcel overlay is the goal, a GL map is
 * the only option — so the dependency buys the boundary layer, not merely a nicer
 * pan.
 *
 * It is LAZY-LOADED (`await import('maplibre-gl')`) because it is ~200KB gzipped
 * and most pages never open a map. The chunk is fetched when a card with real
 * coordinates mounts, so /clients and /invoices pay nothing.
 *
 * CSP: needs `worker-src blob:` (MapLibre rasterises in a Worker built from a blob
 * URL) and `connect-src https://tiles.openfreemap.org` (vector tiles are XHR, NOT
 * <img>, so img-src does not cover them). Both are in src/app.js and asserted
 * against the real response headers in tests/integration/map-geocode.test.js.
 *
 * DEGRADATION — never a broken map, never a crash:
 *   • no latitude/longitude   → "not located yet" + a "Locate on map" action
 *     (staff with property.can_edit) that geocodes via the same-origin proxy and
 *     writes the point back through the ordinary, book-scoped property PUT.
 *   • map library fails to load / no WebGL → the address and coordinates as text.
 *   • point but no parcel tileset for that county → the basemap alone, unannotated.
 */

/** The open, keyless basemap. Styles: liberty (streets), bright, positron. */
const BASEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

/** Building-level — what "at the property level" means. */
const DEFAULT_ZOOM = 17;


/**
 * The org's brand colours, straight off the CSS custom properties `lib/branding`
 * already sets from `org_branding`.
 *
 * Deliberately NOT a setting. The org picks its brand once, in Org Settings, and
 * everything downstream — buttons, the sidebar, now the map — follows. Giving the
 * map its own colour controls would be a second place to configure the same idea,
 * and the two would drift.
 *
 * Falls back to the platform defaults when a var is unset, so an org that has
 * never touched branding still gets a coherent map rather than an unstyled one.
 */
function brandColors() {
  const css = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string) => (css.getPropertyValue(name) || '').trim() || fallback;
  return {
    primary: read('--brand-primary', '#2f5d3a'),
    secondary: read('--brand-secondary', '#5c8a5c'),
  };
}

/**
 * Tint the basemap toward the org's palette.
 *
 * Only WATER and GREEN SPACE are recoloured, and only those: roads, buildings and
 * labels keep their designed contrast, because a street map's job is legibility
 * and a brand wash over the whole thing costs exactly that. This reads as "our
 * map" without becoming unreadable.
 *
 * Every setPaintProperty is individually guarded — OpenFreeMap's layer names are
 * not a contract we control, and a renamed layer must tint less, never throw and
 * take the map down.
 */
function applyBrandTint(map: any, primary: string, secondary: string) {
  let layers: any[] = [];
  try { layers = map.getStyle().layers || []; } catch { return; }

  for (const layer of layers) {
    const id = String(layer.id || '').toLowerCase();
    try {
      if (layer.type === 'fill' && /water|ocean|sea|river|lake/.test(id)) {
        map.setPaintProperty(layer.id, 'fill-color', secondary);
        map.setPaintProperty(layer.id, 'fill-opacity', 0.38);
      } else if (layer.type === 'fill' && /park|wood|forest|grass|golf|pitch|garden|landuse/.test(id)) {
        map.setPaintProperty(layer.id, 'fill-color', primary);
        map.setPaintProperty(layer.id, 'fill-opacity', 0.16);
      } else if (layer.type === 'line' && /water|river|stream/.test(id)) {
        map.setPaintProperty(layer.id, 'line-color', secondary);
      }
    } catch { /* layer renamed or property not applicable — tint less, never throw */ }
  }
}


// ── Raster fallback ──────────────────────────────────────────────────────────
//
// WHY A SECOND MAP RATHER THAN A MESSAGE. Everything that can stop the vector map
// is specific to vector rendering: no WebGL, a blocked or missing Worker, a style
// or glyph host that cannot be reached. NONE of that applies to raster tiles —
// they are plain <img> requests, which the enforced CSP already permits through
// `img-src … https:` with no worker, no XHR and no policy change. So when the GL
// map cannot run, the honest fallback is a DIFFERENT MAP, not a panel apologising
// for the absence of one. "We could not draw a map" is almost never true; it
// nearly always means "we could not draw THIS map".
//
// It is a STREET map, like the vector basemap it stands in for. It used to be aerial
// imagery, which made the two paths look like two different products: a machine
// without WebGL (or a browser blocking it) showed satellite tiles on the Maps tab while
// every other machine showed streets, and that read as "the map style is wrong" rather
// than "this browser fell back" (ATA demo, 2026-09-16). Same provider, street style.

const RASTER_TILE = 256;
const RASTER_COLS = 5;
const RASTER_ROWS = 3;

/** Esri World Street Map — keyless, streets, and attribution-required (rendered below). */
function rasterTileUrl(z: number, x: number, y: number) {
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/${z}/${y}/${x}`;
}

const lonToTileX = (lon: number, z: number) => ((lon + 180) / 360) * 2 ** z;
const latToTileY = (lat: number, z: number) => {
  const r = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z;
};

/**
 * A slippy map built from <img> tiles. No library, no WebGL, no worker — which is
 * exactly the point: it works in the situations that stop the GL map.
 */
function RasterMap({ latitude, longitude, label }: { latitude: number; longitude: number; label: string }) {
  const [zoom, setZoom] = useState(17);
  const z = Math.min(19, Math.max(3, zoom));

  const centerX = lonToTileX(longitude, z);
  const centerY = latToTileY(latitude, z);
  const x0 = Math.floor(centerX) - (RASTER_COLS - 1) / 2;
  const y0 = Math.floor(centerY) - (RASTER_ROWS - 1) / 2;
  const maxIndex = 2 ** z;

  const tiles: { key: string; url: string; left: number; top: number }[] = [];
  for (let dx = 0; dx < RASTER_COLS; dx += 1) {
    for (let dy = 0; dy < RASTER_ROWS; dy += 1) {
      const tx = x0 + dx;
      const ty = y0 + dy;
      if (ty < 0 || ty >= maxIndex) continue;                    // no tile past the poles
      const wrapped = ((tx % maxIndex) + maxIndex) % maxIndex;   // x wraps the globe
      tiles.push({ key: `${z}/${tx}/${ty}`, url: rasterTileUrl(z, wrapped, ty), left: dx * RASTER_TILE, top: dy * RASTER_TILE });
    }
  }
  const pin = { x: (centerX - x0) * RASTER_TILE, y: (centerY - y0) * RASTER_TILE };

  return (
    <div data-testid="property-map-raster" style={{ position: 'relative', height: 380, overflow: 'hidden', borderRadius: 'var(--r-md)', background: '#111' }}>
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: RASTER_COLS * RASTER_TILE, height: RASTER_ROWS * RASTER_TILE, marginLeft: -pin.x, marginTop: -pin.y }}>
        {tiles.map((t) => (
          <img key={t.key} src={t.url} alt="" width={RASTER_TILE} height={RASTER_TILE} draggable={false}
               style={{ position: 'absolute', left: t.left, top: t.top, display: 'block' }} />
        ))}
        <div title={label} style={{
          position: 'absolute', left: pin.x, top: pin.y, width: 18, height: 18, marginLeft: -9, marginTop: -9,
          borderRadius: '50%', background: 'var(--brand-primary, #2f5d3a)', border: '3px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,.45)',
        }} />
      </div>
      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {[['+', 1], ['\u2212', -1]].map(([sym, d]) => (
          <button key={String(sym)} type="button" aria-label={d === 1 ? 'Zoom in' : 'Zoom out'}
                  onClick={() => setZoom((v) => Math.min(19, Math.max(3, v + (d as number))))}
                  style={{ width: 30, height: 30, background: 'rgba(255,255,255,.92)', color: '#222', border: '1px solid rgba(0,0,0,.2)', borderRadius: 'var(--r-sm)', cursor: 'pointer', font: '16px/28px sans-serif', padding: 0 }}>
            {sym}
          </button>
        ))}
      </div>
      {/* Attribution is a licence requirement, not decoration. */}
      <div style={{ position: 'absolute', bottom: 0, right: 0, background: 'rgba(255,255,255,.75)', color: '#222', font: '10px/1.6 sans-serif', padding: '0 6px', borderTopLeftRadius: 'var(--r-sm)' }}>
        Map © Esri
      </div>
    </div>
  );
}

export interface PropertyMapCardProps {
  propertyId: string;
  name: string;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  latitude: number | null;
  longitude: number | null;
  /** Gate the geocode action on the caller's real `property.can_edit`. */
  canEdit?: boolean;
  /** Query keys to invalidate after the coordinates are written back. */
  invalidateKeys?: unknown[][];
  /**
   * Optional parcel-boundary enrichment. When the account number is supplied AND a
   * verified tileset covers this state, the parcel's polygon is outlined on top of
   * the basemap. Absent → the basemap renders alone, which is the ordinary case.
   */
  parcelAccountNumber?: string | null;
  /**
   * The parcel's county. Coverage arrives county-by-county long before statewide,
   * so omitting it answers "unavailable" for counties we can actually draw.
   */
  parcelCounty?: string | null;
}

interface GeocodeResponse {
  enabled: boolean;
  found: boolean;
  latitude?: number;
  longitude?: number;
  matched_address?: string;
  provider?: string;
  query: string;
}

export function PropertyMapCard(props: PropertyMapCardProps) {
  const {
    propertyId, name, addressLine1, city, state, postalCode,
    latitude, longitude, canEdit = false, invalidateKeys = [],
    parcelAccountNumber = null, parcelCounty = null,
  } = props;

  const hasPoint = latitude !== null && longitude !== null
    && Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude));

  const addressText = [addressLine1, city, [state, postalCode].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const locate = useMutation({
    mutationFn: async () => {
      const geo = await api.post<GeocodeResponse>('/api/map/geocode', {
        line1: addressLine1, city, state, postal_code: postalCode,
      });
      if (!geo.enabled) throw new Error('Geocoding is not available on this deployment.');
      if (!geo.found) throw new Error(`Could not locate "${geo.query || addressText || name}".`);
      // Persist through the ORDINARY property write — org-scoped, book-scoped,
      // permission-gated and audited. The geocode proxy deliberately does not write.
      await api.put(`/api/properties/${propertyId}`, {
        latitude: geo.latitude, longitude: geo.longitude,
      });
      return geo;
    },
    onSuccess: (geo) => {
      toast({ variant: 'success', message: `Located at ${geo.matched_address || geo.query}` });
      for (const key of invalidateKeys) queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err: unknown) => {
      toast({ variant: 'error', message: (err as Error).message || 'Could not locate this property.' });
    },
  });

  // NO PRECISE POINT — still show a map. Falling back to a dashed panel here was
  // "defaulting to nothing", which is the whole thing this work removes. The state
  // view is honest and orienting; it is labelled approximate and carries NO pin,
  // because there is no point to pin. "Locate on map" then refines it in place.
  if (!hasPoint) {
    const coarse = fallbackViewFor(state);
    const locateAction = canEdit ? (
      <Button
        variant="primary"
        size="sm"
        loading={locate.isPending}
        disabled={!addressText}
        onClick={() => locate.mutate()}
      >
        {addressText ? 'Locate on map' : 'Add an address first'}
      </Button>
    ) : null;

    // Not even a state to fall back on — then there is genuinely nothing to draw.
    if (!coarse) {
      return (
        <MapPanel title="Location">
          <Placeholder
            heading="Not located yet"
            body={<>This property has no address or state on file, so there is nothing to plot.</>}
            address={addressText}
            action={locateAction}
          />
        </MapPanel>
      );
    }

    return (
      <MapPanel title="Location" action={locateAction}>
        <StreetMap
          latitude={coarse.lat}
          longitude={coarse.lon}
          zoom={STATE_ZOOM}
          showPin={false}
          label={name}
          address={addressText}
          state={state}
          parcelAccountNumber={null}
          parcelCounty={null}
          approximate
        />
      </MapPanel>
    );
  }

  return (
    <MapPanel
      title="Location"
      action={
        canEdit ? (
          <Button variant="ghost" size="sm" loading={locate.isPending} onClick={() => locate.mutate()}>
            Re-locate
          </Button>
        ) : null
      }
    >
      <StreetMap
        latitude={Number(latitude)}
        longitude={Number(longitude)}
        label={name}
        address={addressText}
        state={state}
        parcelAccountNumber={parcelAccountNumber}
        parcelCounty={parcelCounty}
      />
    </MapPanel>
  );
}

// ── The map itself ───────────────────────────────────────────────────────────

function StreetMap({
  latitude, longitude, label, address, state, parcelAccountNumber, parcelCounty,
  zoom = DEFAULT_ZOOM, showPin = true, approximate = false,
}: {
  latitude: number; longitude: number; label: string; address: string;
  state: string | null; parcelAccountNumber: string | null; parcelCounty: string | null;
  /** Building-level by default; state-level when the point is only a fallback. */
  zoom?: number;
  /** A pin asserts "the property is HERE" — never shown for an approximate view. */
  showPin?: boolean;
  /** Renders the honest caption instead of a precise coordinate readout. */
  approximate?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<{ remove: () => void } | null>(null);
  const [failed, setFailed] = useState(false);
  /**
   * What the boundary overlay actually did. `no-match` is the important one: the
   * county file loaded and drew, but this parcel's account number is not in it.
   * Left unsaid, that is indistinguishable from the overlay working — boundaries
   * everywhere and no highlight — which is the silent failure this whole surface
   * is designed around. So it gets said.
   */
  const [overlay, setOverlay] = useState<'matched' | 'no-match' | 'unavailable' | null>(null);

  useEffect(() => {
    let cancelled = false;
    let resizeObs: ResizeObserver | null = null;
    const el = containerRef.current;
    if (!el) return undefined;

    (async () => {
      try {
        // No WebGL means the GL map can never run. Skip it entirely rather than
        // constructing one that will fail — the raster map is the better answer
        // and the user should not watch the wrong one fail first.
        const probe = document.createElement('canvas');
        if (!probe.getContext('webgl2') && !probe.getContext('webgl')) { setFailed(true); return; }

        const maplibre = await import('maplibre-gl');
        // jsdom and any WebGL-less environment: bail to the text fallback rather
        // than letting the constructor throw inside an effect, which React
        // escalates to the page-level error boundary.
        if (cancelled || typeof maplibre.Map !== 'function') return;

        const brand = brandColors();
        const map = new maplibre.Map({
          container: el,
          style: BASEMAP_STYLE,
          center: [longitude, latitude],   // GL order is [lng, lat]
          zoom,
          attributionControl: { compact: true },
        });
        mapRef.current = map as unknown as { remove: () => void };

        // ⚠️ THIS IS WHY THE MAP WAS BLANK. MapLibre measures its container ONCE, at
        // construction, and falls back to 400x300 when that measurement is zero —
        // then never re-measures, because it only listens to WINDOW resize, not to
        // its own container. This card lives inside a Tabs panel, so on any tab but
        // the active one the container is display:none and measures zero. The map
        // locked to 400x300 inside a full-width panel and requested almost no tiles.
        // Observed directly: container 1280x720, canvas 400x300.
        //
        // A ResizeObserver fixes the whole class — first layout, tab switch, sidebar
        // collapse, window resize — rather than the one case that was reported.
        resizeObs = new ResizeObserver(() => { try { map.resize(); } catch { /* torn down */ } });
        resizeObs.observe(el);

        map.addControl(new maplibre.NavigationControl({ showCompass: false }), 'top-right');

        // A failed style/tile fetch must degrade, not spam the console forever.
        // ⚠️ ONLY A FATAL ERROR MAY REPLACE THE MAP.
        //
        // MapLibre fires `error` for plenty of survivable things: a single tile
        // that 404s, a sprite image the style references but the spritesheet does
        // not carry (OpenFreeMap's liberty style does exactly this — "Image
        // 'office' could not be loaded"). Treating ANY of those as failure swapped
        // a working map for the "could not be displayed" panel, which is what the
        // deployed demo showed while the map underneath was fine.
        //
        // So: an error only counts if the style never became usable. Once the
        // style has loaded, the map is real and a per-tile hiccup must not take it
        // down — a partially-drawn map beats a panel saying there is none.
        // ⚠️ maplibre-gl 6 narrowed this overload: the listener is typed
        // `(ev: ErrorEvent & Object) => void`, and the old hand-written
        // `{ error?: Error }` no longer satisfies it. Let the library supply the
        // parameter type instead of restating it — the body already treats
        // `e.error` as optional, so behaviour is unchanged.
        map.on('error', (e) => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.warn('[map]', e && e.error ? e.error.message : e);
          if (!map.isStyleLoaded()) setFailed(true);
        });

        // A pin is a claim that the property is exactly there. On the state-level
        // fallback that claim would be false, so no marker is drawn at all.
        if (showPin) {
          new maplibre.Marker({ color: brand.primary })
            .setLngLat([longitude, latitude])
            .setPopup(new maplibre.Popup({ offset: 24 }).setText(label || address || 'Property'))
            .addTo(map);
        }

        map.on('load', () => {
          if (cancelled) return;
          applyBrandTint(map, brand.primary, brand.secondary);
          if (!parcelAccountNumber || !state) return;
          void addParcelOverlay(map, maplibre, state, parcelCounty, parcelAccountNumber, brand)
            .then((status) => { if (!cancelled) setOverlay(status); });
        });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      try { resizeObs?.disconnect(); } catch { /* nothing to disconnect */ }
      try { mapRef.current?.remove(); } catch { /* already torn down */ }
      mapRef.current = null;
    };
  }, [latitude, longitude, label, address, state, parcelAccountNumber, parcelCounty, zoom, showPin]);

  return (
    <div>
      {/* The address sits ABOVE the map. It is the label for what you are looking
          at, and burying it underneath meant scrolling past the map to find out
          which property this even is. */}
      <p style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', fontWeight: 500 }}>
        {address || 'No address on file'}
        {approximate ? (
          <span data-testid="map-approximate" style={{ marginLeft: 'var(--sp-3)', fontWeight: 400, color: 'var(--color-status-amber, #b7791f)' }}>
            Approximate — showing {state}. This property has not been located yet.
          </span>
        ) : (
          <span style={{ marginLeft: 'var(--sp-3)', fontWeight: 400, fontFamily: 'var(--font-mono, monospace)', color: 'var(--color-ink-3)' }}>
            {latitude.toFixed(5)}, {longitude.toFixed(5)}
          </span>
        )}
      </p>

      {failed ? (
        // NOT a message — a different map. See RasterMap's header: everything that
        // can stop the vector map is vector-specific, and raster tiles are immune
        // to all of it.
        <RasterMap latitude={latitude} longitude={longitude} label={label} />
      ) : (
        <div
          ref={containerRef}
          data-testid="property-map"
          role="application"
          aria-label={`Map of ${label}`}
          style={{ height: 380, borderRadius: 'var(--r-md)', overflow: 'hidden', background: '#e8e8e4' }}
        />
      )}


      {overlay === 'matched' && (
        <p data-testid="overlay-matched" style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          Parcel boundary shown from the county record.
        </p>
      )}
      {overlay === 'no-match' && (
        <p data-testid="overlay-no-match" style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-status-amber, #b7791f)' }}>
          Boundaries are from the county file, but account {parcelAccountNumber} is not
          in it — so no parcel is outlined. The account number may differ from the
          appraisal district&rsquo;s.
        </p>
      )}
    </div>
  );
}

/**
 * Outline the parcel's own boundary over the basemap, when we have tiles for it.
 *
 * The tiles are ours, served same-origin from R2 through /api/map/tiles, and carry
 * GEOMETRY plus the join field ONLY — never owner names or values. A tile cannot
 * know who is looking at it, so anything inside one has left the access-control
 * boundary.
 *
 * ⚠️ A WRONG JOIN FIELD FAILS SILENTLY: every boundary in the county draws
 * perfectly and none of ours highlight. There is no error and nothing looks broken
 * in a demo. That is why the server answers `available:false` for any state whose
 * join field is unverified, rather than serving tiles that would draw beautifully
 * and match nothing.
 */
async function addParcelOverlay(
  map: any, maplibre: any, state: string, county: string | null, accountNumber: string,
  brand: { primary: string; secondary: string },
): Promise<'matched' | 'no-match' | 'unavailable'> {
  const brandPrimary = brand.primary;
  try {
    const meta = await api.get<{ available: boolean; url?: string; joinField?: string }>(
      `/api/map/tiles/${encodeURIComponent(state)}${county ? `?county=${encodeURIComponent(county)}` : ''}`,
    );
    if (!meta.available || !meta.url || !meta.joinField) return 'unavailable';

    const { Protocol } = await import('pmtiles');
    const protocol = new Protocol();
    maplibre.addProtocol('pmtiles', protocol.tile);

    map.addSource('parcels', { type: 'vector', url: `pmtiles://${meta.url}` });

    // Every boundary, faint — context for where the parcel sits on its block.
    map.addLayer({
      id: 'parcel-lines',
      type: 'line',
      source: 'parcels',
      'source-layer': 'parcels',
      paint: { 'line-color': brand.secondary, 'line-width': 0.8, 'line-opacity': 0.6 },
    });

    // Ours, emphasised. The filter is the join — see the silent-failure note above.
    map.addLayer({
      id: 'parcel-highlight',
      type: 'fill',
      source: 'parcels',
      'source-layer': 'parcels',
      filter: ['==', ['get', meta.joinField], accountNumber],
      paint: { 'fill-color': brandPrimary, 'fill-opacity': 0.22 },
    });

    // A CRISP EDGE ON TOP OF THE FILL. `fill-outline-color` renders a hairline that
    // disappears against a busy basemap, and this boundary is the entire point of
    // the overlay — the one thing on screen that says "this parcel, not its
    // neighbours". A translucent wash alone reads as a shadow.
    map.addLayer({
      id: 'parcel-highlight-outline',
      type: 'line',
      source: 'parcels',
      'source-layer': 'parcels',
      filter: ['==', ['get', meta.joinField], accountNumber],
      paint: { 'line-color': brandPrimary, 'line-width': 2.5, 'line-opacity': 0.95 },
    });

    // DID THE JOIN ACTUALLY MATCH? Asked once the map settles, because a filter
    // that matches nothing renders exactly like one that has not drawn yet. This
    // is the difference between "your parcel is outlined" and "we drew the whole
    // county and found nothing of yours", and the operator has to be told which.
    return await new Promise((resolve) => {
      map.once('idle', () => {
        try {
          const hits = map.queryRenderedFeatures({ layers: ['parcel-highlight'] });
          resolve(hits && hits.length ? 'matched' : 'no-match');
        } catch {
          resolve('no-match');
        }
      });
    });
  } catch {
    // Enrichment only. A missing or malformed tileset must never take the basemap
    // down with it — the property still has a pin and an address.
    return 'unavailable';
  }
}

// ── Small presentational helpers (kept local — nothing else needs them) ──────

function MapPanel({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-4)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--sp-3)', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-md)', color: 'var(--color-ink)' }}>{title}</h3>
        {action}
      </header>
      {children}
    </section>
  );
}

function Placeholder({
  heading, body, address, action,
}: {
  heading: string; body: ReactNode; address: string; action?: ReactNode;
}) {
  return (
    <div
      style={{
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-5)',
        textAlign: 'center',
        color: 'var(--color-ink-3)',
      }}
    >
      <p style={{ margin: '0 0 var(--sp-2)', color: 'var(--color-ink)', fontWeight: 500 }}>{heading}</p>
      <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)' }}>{body}</p>
      <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink)' }}>{address || 'No address on file'}</p>
      {action}
    </div>
  );
}
